// src/pages/healthcare/results/testOrderComponents/ResultReviewPanel.tsx
//
// Inline review/update panel for a single TestResult. This is the relevant part
// of the old ReviewResultModal (result notes + the review & update form) rendered
// inline inside an expandable row, so we no longer need a separate result viewer
// modal. Patient / test-order context is intentionally omitted — that lives at the
// top of the order sheet / in-tray row.

import React, { useState, useEffect, useCallback } from "react";
import { Space, Input, Checkbox, Select, Button, Typography } from "antd";
import { SaveOutlined, WarningOutlined } from "@ant-design/icons";
import { format } from "date-fns";

import { updateTestResult } from "../../../../api/healthcare/Testing/TestResultApi";

import { TestResult } from "../../../../types/healthcare/TestTypes";
import { useNotifications } from "../../../../context/NotificationContext";

const { TextArea } = Input;
const { Text } = Typography;

// Practitioner-settable statuses only. `pending`, `in_progress` and `completed`
// are system-set markers written by the upload/analysis pipeline — offering
// `Completed` here read as "done" while leaving the order in the In Tray.
// "Results available" stays: it is the ONLY way to un-review a result, and without
// it review would be a one-way door with no UI escape.
// `updateTestResult` still accepts all six server-side — this narrowing is a UI
// decision, not a change to what the system can write.
const STATUS_OPTIONS = [
	{ label: "Reviewed", value: "reviewed" },
	{ label: "Results available (not yet reviewed)", value: "results_available" },
	{ label: "Cancelled", value: "cancelled" },
];

interface ResultReviewPanelProps {
	result: TestResult;
	testId?: number;
	onSaved?: () => void;
}

const ResultReviewPanel: React.FC<ResultReviewPanelProps> = ({ result, testId, onSaved }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [resultNotes, setResultNotes] = useState<string>("");
	const [isAbnormal, setIsAbnormal] = useState<boolean>(false);
	const [requiresFollowup, setRequiresFollowup] = useState<boolean>(false);
	const [followupNotes, setFollowupNotes] = useState<string>("");
	const [newStatus, setNewStatus] = useState<string>("reviewed");

	const { showError, showSuccess } = useNotifications();

	useEffect(() => {
		if (result) {
			setResultNotes(result.result_notes || "");
			setIsAbnormal(result.is_abnormal || false);
			setRequiresFollowup(result.requires_followup || false);
			setFollowupNotes(result.followup_notes || "");

			if (result.status === "pending" || result.status === "completed" || result.status === "results_available") {
				setNewStatus("reviewed");
			} else {
				// A status the narrowed dropdown no longer offers (e.g. `in_progress`)
				// would render the Select blank — fall back to the default action.
				setNewStatus(
					STATUS_OPTIONS.some((option) => option.value === result.status) ? result.status : "reviewed",
				);
			}
		}
	}, [result]);

	const handleSave = useCallback(async () => {
		if (!testId || !result?.test_type) {
			showError("Invalid result data");
			return;
		}

		setIsSubmitting(true);
		try {
			const updateData: Partial<TestResult> = {
				status: newStatus,
				result_notes: resultNotes,
				is_abnormal: isAbnormal,
				requires_followup: requiresFollowup,
				followup_notes: requiresFollowup ? followupNotes : undefined,
			};

			await updateTestResult(testId, result.test_type, updateData);
			showSuccess("Result updated successfully");
			onSaved?.();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update result. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	}, [testId, result, resultNotes, isAbnormal, requiresFollowup, followupNotes, newStatus, onSaved, showError, showSuccess]);

	return (
		<Space direction="vertical" style={{ width: "100%" }} size={12}>
			{result.results_reviewed_date && (
				<Text type="secondary" style={{ fontSize: 11 }}>
					Previously reviewed: {format(new Date(result.results_reviewed_date), "dd/MM/yyyy HH:mm")}
				</Text>
			)}

			{result.result_notes && (
				<div>
					<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Existing notes</Text>
					<div style={{ padding: "8px 10px", borderRadius: 4, fontSize: 12, whiteSpace: "pre-wrap", background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
						{result.result_notes}
					</div>
				</div>
			)}

			<div>
				<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Update status</Text>
				<Select value={newStatus} onChange={setNewStatus} options={STATUS_OPTIONS} style={{ width: "100%" }} size="small" />
			</div>

			<div>
				<Checkbox checked={isAbnormal} onChange={(e) => setIsAbnormal(e.target.checked)}>
					<Text style={{ fontSize: 12, fontWeight: 500 }}>Mark as abnormal</Text>
				</Checkbox>
				<Text type="secondary" style={{ display: "block", fontSize: 11, marginTop: 2, marginLeft: 24 }}>
					Check if the result is outside normal range
				</Text>
			</div>

			{isAbnormal && (
				<div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 4, background: "var(--mg-warning-bg)", border: "1px solid var(--mg-warning)" }}>
					<WarningOutlined style={{ color: "var(--mg-warning)", fontSize: 13 }} />
					<Text style={{ fontSize: 12, color: "var(--mg-warning)" }}>
						Abnormal result — may require clinical follow-up.
					</Text>
				</div>
			)}

			<div>
				<Checkbox checked={requiresFollowup} onChange={(e) => setRequiresFollowup(e.target.checked)}>
					<Text style={{ fontSize: 12, fontWeight: 500 }}>Requires follow-up</Text>
				</Checkbox>
				<Text type="secondary" style={{ display: "block", fontSize: 11, marginTop: 2, marginLeft: 24 }}>
					Check if the patient needs a follow-up action
				</Text>
			</div>

			{requiresFollowup && (
				<div>
					<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Follow-up notes</Text>
					<TextArea
						value={followupNotes}
						onChange={(e) => setFollowupNotes(e.target.value)}
						placeholder="Describe the required follow-up actions…"
						rows={3}
					/>
				</div>
			)}

			<div>
				<Text type="secondary" style={{ display: "block", fontSize: 11, marginBottom: 4 }}>Review notes</Text>
				<TextArea
					value={resultNotes}
					onChange={(e) => setResultNotes(e.target.value)}
					placeholder="Add your review notes here…"
					rows={4}
				/>
				<Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
					These notes will be appended to any existing notes
				</Text>
			</div>

			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<Button type="primary" size="small" icon={<SaveOutlined />} loading={isSubmitting} onClick={handleSave}>
					Save &amp; Update
				</Button>
			</div>
		</Space>
	);
};

export default ResultReviewPanel;
